import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp, Check, Plus } from 'lucide-react';
import { IoIosSearch } from "react-icons/io";
import { fmt } from '../../../../lib/formatters';
import api from '../../../../services/api';
import QuotationPreview from './QuotationPreview';
import MeasurementPricingBlock from '../components/MeasurementPricingBlock';

// Custom Accordion Component
const Section = ({ title, rightElement, expanded, onToggle, children }) => (
  <div className="border border-slate-200 rounded-lg bg-white mb-2 shadow-sm">
    <div 
      className={`px-3 py-2.5 bg-slate-50/80 cursor-pointer flex justify-between items-center transition-colors hover:bg-slate-100/80 ${expanded ? 'rounded-t-lg' : 'rounded-lg'}`}
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{title}</span>
        {rightElement}
      </div>
      {expanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
    </div>
    {expanded && (
      <div className="p-3 border-t border-slate-200 space-y-3 bg-white">
        {children}
      </div>
    )}
  </div>
);

const InitialQuotationForm = ({ initialData = {}, onSubmit, onCancel, loading = false, isFinal = false, commonNotes, notesValue }) => {
  const [availableItems, setAvailableItems] = useState([]);
  const [blumStockItems, setBlumStockItems] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  
  const [roomItems, setRoomItems] = useState({});
  const [focusedRow, setFocusedRow] = useState(null); // { room, tempId }
  
  const [isPreview, setIsPreview] = useState(false);
  const [siteVisitRooms, setSiteVisitRooms] = useState({});
  const [roomsList, setRoomsList] = useState([]);
  const [expandedSection, setExpandedSection] = useState('rooms');
  
  const [searchPriceActive, setSearchPriceActive] = useState(false);
  const [priceSearchQuery, setPriceSearchQuery] = useState("");
  const [copiedPriceId, setCopiedPriceId] = useState(null);

  useEffect(() => {
    const fetchPurchasedItems = async () => {
      try {
        const res = await api.get('/inventory-master?limit=10000');
        const items = res.data?.data || res.data || [];
        setAvailableItems(items);
      } catch (error) {
        console.error("Error fetching items for quotation:", error);
      }
    };

    const fetchBlumStock = async () => {
      try {
        const res = await api.get('/purchase-master?limit=10000');
        const items = res.data?.data || res.data || [];
        setBlumStockItems(items);
      } catch (error) {
        console.error("Error fetching purchase master items:", error);
      }
    };

    const fetchSiteVisitData = async () => {
      try {
        let measurements = initialData.siteVisitData?.measurements || [];
        
        try {
          const res = await api.get(`/verve-leads/${initialData._id}/site-visit`);
          if (res.data?.data?.measurements) {
            measurements = res.data.data.measurements;
          }
        } catch (err) {
          console.warn("Could not fetch latest site visit data, using initialData if available");
        }
        
        const grouped = measurements.reduce((acc, curr) => {
          if (!acc[curr.room]) acc[curr.room] = [];
          acc[curr.room].push(curr);
          return acc;
        }, {});
        
        setSiteVisitRooms(grouped);
        
        const rList = Object.keys(grouped);
        
        const savedRawItems = initialData.items || initialData.quotationData?.items || initialData.quotationItems || initialData.initialQuotationItems || [];
        const initialRoomsState = {};

        if (savedRawItems && savedRawItems.length > 0) {
          // Reconstruct from previously saved data
          savedRawItems.forEach(roomData => {
            const roomName = roomData.room;
            if (!rList.includes(roomName)) rList.push(roomName);
            
            // Group items by measurementInfo
            const blocksByMeas = {};
            
            (roomData.items || []).forEach(item => {
              const measKey = item.measurementInfo || 'Custom';
              if (!blocksByMeas[measKey]) {
                // Try to reconstruct measurement object
                let measurement = null;
                if (measKey !== 'Custom') {
                  const parts = measKey.split(' | ');
                  const dims = parts[2] ? parts[2].match(/H:(\d+)\s+W:(\d+)\s+D:(\d+)/) : null;
                  measurement = {
                    zone: parts[0] || '',
                    particulars: parts[1] || '',
                    height: dims ? dims[1] : '',
                    width: dims ? dims[2] : '',
                    depth: dims ? dims[3] : ''
                  };
                }
                blocksByMeas[measKey] = {
                  tempId: 'block-' + Date.now() + Math.random(),
                  measurement,
                  priceListItems: [],
                  blumItems: [],
                  customItems: []
                };
              }
              
              const block = blocksByMeas[measKey];
              const amount = parseFloat(item.unitPrice || item.amount || 0);
              
              if (item.category === 'priceListItems') {
                let description = item.name;
                let internalType = '';
                let shutterType = '';
                let width = '';
                let height = '';
                let depth = '';
                
                const match = item.name.match(/(.*?) - (.*?) - (.*?) \[(.*?)x(.*?)x(.*?)\]/);
                if (match) {
                  description = match[1] === 'Item' ? '' : match[1];
                  internalType = match[2] === 'N/A' ? '' : match[2];
                  shutterType = match[3] === 'N/A' ? '' : match[3];
                  width = match[4];
                  height = match[5];
                  depth = match[6];
                }
                
                block.priceListItems.push({
                  tempId: 'pl-' + Date.now() + Math.random(),
                  description, internalType, shutterType, width, height, depth, amount
                });
              } else if (item.category === 'blumItems') {
                block.blumItems.push({
                  tempId: 'blum-' + Date.now() + Math.random(),
                  name: item.name, amount,
                  _id: item._id || item.inventoryId,
                  inventoryId: item.inventoryId || item._id,
                  rawData: item.rawData || item
                });
              } else {
                block.customItems.push({
                  tempId: 'custom-' + Date.now() + Math.random(),
                  name: item.name, amount
                });
              }
            });
            
            let blocks = Object.values(blocksByMeas);
            if (blocks.length === 0) {
              blocks = [{
                tempId: 'custom-block-' + Date.now() + Math.random(),
                measurement: null,
                priceListItems: [],
                blumItems: [],
                customItems: []
              }];
            }
            initialRoomsState[roomName] = blocks;
          });
          
          // Ensure all rooms from site visit are initialized with at least one block
          rList.forEach(room => {
            if (!initialRoomsState[room]) {
              const measList = grouped[room] || [];
              if (measList.length > 0) {
                initialRoomsState[room] = measList.map(meas => ({
                  tempId: meas._id || Math.random().toString(),
                  measurement: meas,
                  priceListItems: [{ tempId: 'pl-' + Date.now() + Math.random(), description: '', internalType: '', shutterType: '', width: '', height: '', depth: '', amount: 0 }],
                  blumItems: [{ tempId: 'blum-' + Date.now() + Math.random(), name: '', amount: 0 }],
                  customItems: []
                }));
              } else {
                initialRoomsState[room] = [{
                  tempId: 'custom-block-' + Date.now() + Math.random(),
                  measurement: null,
                  priceListItems: [],
                  blumItems: [],
                  customItems: []
                }];
              }
            }
          });
        } else {
          // Initialize fresh from site visit grouped
          rList.forEach(room => {
            const measList = grouped[room] || [];
            if (measList.length > 0) {
              initialRoomsState[room] = measList.map(meas => ({
                tempId: meas._id || Math.random().toString(),
                measurement: meas,
                priceListItems: [{ tempId: 'pl-' + Date.now() + Math.random(), description: '', internalType: '', shutterType: '', width: '', height: '', depth: '', amount: 0 }],
                blumItems: [{ tempId: 'blum-' + Date.now() + Math.random(), name: '', amount: 0 }],
                customItems: []
              }));
            } else {
              initialRoomsState[room] = [{
                tempId: 'custom-block-' + Date.now() + Math.random(),
                measurement: null,
                priceListItems: [],
                blumItems: [],
                customItems: []
              }];
            }
          });
        }
        
        setRoomsList(rList);
        setRoomItems(initialRoomsState);
      } catch (error) {
        console.error("Error setting up quotation data:", error);
      }
    };
    
    Promise.all([fetchPurchasedItems(), fetchBlumStock(), fetchSiteVisitData()]).finally(() => {
      setIsFetching(false);
    });
  }, [initialData._id, initialData.siteVisitData]);

  const calculateRoomTotal = (room) => {
    return (roomItems[room] || []).reduce((sum, item) => {
      const sumCategory = (cat) => (item[cat] || []).reduce((s, i) => s + parseFloat(i.amount || 0), 0);
      return sum + sumCategory('priceListItems') + sumCategory('blumItems') + sumCategory('customItems');
    }, 0);
  };

  const calculateGrandTotal = () => {
    return Object.keys(roomItems).reduce((sum, room) => sum + calculateRoomTotal(room), 0);
  };

  const handleAddEmptyRow = (room) => {
    setRoomItems(prev => ({
      ...prev,
      [room]: [...(prev[room] || []), {
        tempId: 'custom-block-' + Date.now(),
        measurement: null,
        priceListItems: [{ tempId: 'pl-' + Date.now() + Math.random(), description: '', internalType: '', shutterType: '', width: '', height: '', depth: '', amount: 0 }],
        blumItems: [{ tempId: 'blum-' + Date.now() + Math.random(), name: '', amount: 0 }],
        customItems: []
      }]
    }));
  };

  const handleRemoveBlock = (room, tempId) => {
    setRoomItems(prev => ({
      ...prev,
      [room]: prev[room].filter(i => i.tempId !== tempId)
    }));
  };

  const handleUpdateBlock = (room, updatedBlock) => {
    setRoomItems(prev => ({
      ...prev,
      [room]: prev[room].map(i => i.tempId === updatedBlock.tempId ? updatedBlock : i)
    }));
  };

  const [isAssignment, setIsAssignment] = useState(false);

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    
    const hasAnyItems = Object.values(roomItems).some(items => items.length > 0);
    if (!hasAnyItems) {
      toast.error("Please add at least one item.");
      return;
    }

    setIsPreview(true);
  };

  const handleProceedToAssignment = () => {
    // setIsAssignment(true);
    handleFinalSubmit({}); // Bypass assignment form
  };

  const [overrideGrandTotal, setOverrideGrandTotal] = useState('');

  const handleFinalSubmit = async (approvalData) => {
    const calculatedTotal = calculateGrandTotal();
    const finalAmount = overrideGrandTotal !== '' ? parseFloat(overrideGrandTotal) || 0 : calculatedTotal;
    
    // We need to pass the structured data to the backend.
    // Ensure the backend logic can handle structured data or we flatten it if needed.
    const structuredItems = roomsList.map(room => {
      const roomItemsFlattened = [];
      
      roomItems[room].forEach(block => {
        const measurementInfo = block.measurement 
          ? `${block.measurement.zone || ''} | ${block.measurement.particulars || ''} | H:${block.measurement.height} W:${block.measurement.width} D:${block.measurement.depth}` 
          : 'Custom';
        
        ['priceListItems', 'blumItems', 'customItems'].forEach(cat => {
          (block[cat] || []).forEach(item => {
            if (item.name?.trim() || item.description?.trim()) {
              const details = item.rawData || item || {};
              roomItemsFlattened.push({
                inventoryId: details.inventoryId || details._id || details.id || item.inventoryId || item._id || item.id || null,
                name: cat === 'priceListItems' ? `${item.description || 'Item'} - ${item.internalType || 'N/A'} - ${item.shutterType || 'N/A'} [${item.width || 0}x${item.height || 0}x${item.depth || 0}]` : item.name,
                unitPrice: item.amount,
                quantity: 1,
                measurementInfo: measurementInfo,
                category: cat,
                description: details.description || details.itemName || item.name || '',
                articleId: details.articleId || details.itemCode || '',
                vendorName: details.vendorName || details.vendor?.name || details.supplier?.name || details.vendor || details.supplier || '',
                subCategory: details.subCategory || '',
                information: details.information || '',
                internalType: details.internalType || item.internalType || '',
                shutterType: details.shutterType || item.shutterType || '',
                width: details.width || item.width || '',
                height: details.height || item.height || '',
                depth: details.depth || item.depth || '',
                uom: details.uom || '',
                moq: details.moq || '',
                purchasedQty: details.purchasedQty || '',
                availableQty: Number(details.availableQty ?? details.stock ?? details.quantity ?? 0),
                purchaseAmount: details.purchaseRate || 0,
                vervePrice: details.vervePrice || item.amount || 0,
                taxableAmount: details.taxableAmount || 0,
                gstPercentage: details.gstPercentage || 0,
                gstAmount: details.gstAmount || 0,
                invoiceNumber: details.invoiceNumber || '',
                remarks: details.remark || details.remarks || '',
                date: details.date || null,
                version: details.version || '',
                imageUrl: details.imageUrl || '',
                netAmount: details.netAmount || item.amount || 0
              });
            }
          });
        });
      });

      return {
        room,
        items: roomItemsFlattened
      };
    });

    onSubmit({
      items: structuredItems,
      quotationAmount: finalAmount,
      isGroupedByRoom: true, // flag for the backend to know it's the new structure
    });
  };

  if (isAssignment) {
    // Import dynamically or ensure it's imported at the top
    const ApprovalAssignmentForm = require('./ApprovalAssignmentForm').default;
    return (
      <ApprovalAssignmentForm
        isOpen={true}
        onClose={() => setIsAssignment(false)}
        initialData={initialData}
        onSubmit={handleFinalSubmit}
        loading={loading}
      />
    );
  }

  if (isPreview) {
    return (
      <QuotationPreview 
        initialData={initialData}
        roomItems={roomItems}
        calculateRoomTotal={calculateRoomTotal}
        calculateGrandTotal={calculateGrandTotal}
        onCancel={() => setIsPreview(false)}
        onSubmit={handleProceedToAssignment}
        loading={loading}
        isFinal={isFinal}
        notesValue={notesValue}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2 p-1">
      {/* Fixed Header Section */}
      <div className="z-50">
        <div className="flex items-center justify-between border-b border-slate-100 pb-1 mb-1 relative">
          <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            01. SELECT ROOMS REQUIRED
          </h3>
          {/*
          <button 
            type="button" 
            onClick={() => setSearchPriceActive(!searchPriceActive)} 
            className={`flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded transition-colors shadow-sm border ${searchPriceActive ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <IoIosSearch size={14} /> Search Price
          </button>
          
          {searchPriceActive && (
            <div className="absolute top-full right-0 mt-2 w-full sm:w-[350px] p-3 bg-white border border-slate-200 rounded-lg flex flex-col gap-2 shadow-2xl z-[60]">
               <input 
                 type="text" 
                 placeholder="Search any item in database to find its price..." 
                 value={priceSearchQuery}
                 onChange={(e) => setPriceSearchQuery(e.target.value)}
                 className="w-full h-8 text-[12px] bg-slate-50 border border-slate-200 rounded px-3 focus:outline-none focus:border-slate-400 focus:bg-white shadow-sm transition-colors"
               />
             {priceSearchQuery.trim().length > 1 && (
               <div className="max-h-48 overflow-y-auto bg-white border border-slate-100 rounded shadow-inner custom-scrollbar">
                 {availableItems
                   .filter(ai => 
                     (ai.itemName || ai.name || ai.description || ai.internalType || ai.shutterType || '').toLowerCase().includes(priceSearchQuery.toLowerCase()) || 
                     (ai.articleId || ai.itemCode || '').toLowerCase().includes(priceSearchQuery.toLowerCase()) ||
                     (ai.materialDescription || '').toLowerCase().includes(priceSearchQuery.toLowerCase())
                   )
                   .slice(0, 20)
                   .map((ai, idx) => {
                     const displayName = ai.articleId 
                       ? `${ai.articleId} - ${ai.materialDescription}` 
                       : (ai.itemName || ai.description || ai.internalType || ai.shutterType || ai.name || 'Item');
                     const rate = ai.price || ai.mrp || ai.purchaseRate || ai.rate || 0;
                     return (
                       <div key={idx} className="flex items-center justify-between px-3 py-2 border-b border-slate-50 hover:bg-slate-50 last:border-0">
                         <div className="flex flex-col min-w-0 pr-2">
                           <span className="text-[11px] font-bold text-slate-700 truncate">{displayName}</span>
                           <span className="text-[9px] text-slate-400">{ai.articleId || ai.itemCode || ai.category || 'No Code'}</span>
                         </div>
                         <div className="flex items-center gap-2 shrink-0">
                           <span className="text-[11px] font-bold text-slate-900">₹{fmt(rate)}</span>
                           <button 
                             type="button" 
                             onClick={() => {
                               navigator.clipboard.writeText(String(rate));
                               setCopiedPriceId(displayName + idx);
                               setTimeout(() => setCopiedPriceId(null), 2000);
                             }}
                             className="text-[9px] font-bold px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors"
                           >
                             {copiedPriceId === (displayName + idx) ? 'Copied!' : 'Copy'}
                           </button>
                         </div>
                       </div>
                     );
                   })}
               </div>
             )}
          </div>
        )}
        */}
      </div>

      <div className="flex flex-wrap gap-2">
          {roomsList.map(room => {
            const isSelected = true; // For initial quotation, these are always selected from site visit
            return (
              <button
                key={room}
                type="button"
                onClick={() => {
                  setExpandedSection(prev => prev === room ? null : room);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold transition-all duration-200 border shadow-sm ${
                  isSelected 
                    ? 'bg-slate-900 border-slate-900 text-white' 
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Check size={14} className="text-white" />
                {room}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scrollable Content Section */}
      <div className="space-y-4 h-[260px] overflow-y-auto pr-2 custom-scrollbar z-0 relative">

        {roomsList.length === 0 ? (
          <div className="p-8 text-center bg-slate-50 rounded-xl border border-slate-200">
            <p className="text-slate-500 text-sm font-medium">No site visit measurements found for this lead. Please complete the Site Visit step first.</p>
          </div>
        ) : (
          roomsList.map(room => {
            return (
              <Section 
                key={room}
                title={`${room}`}
                expanded={expandedSection === room}
                onToggle={() => setExpandedSection(prev => prev === room ? null : room)}
                rightElement={
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                      Total: ₹{fmt(calculateRoomTotal(room))}
                    </span>
                  </div>
                }
              >
                {/* Unified Site Visit and Quotation Items Row */}
                <div className="flex flex-col gap-2">
                  {(roomItems[room] || []).map((item, idx) => (
                    <MeasurementPricingBlock 
                      key={item.tempId}
                      measurementItem={item}
                      availableItems={availableItems}
                      blumStockItems={blumStockItems}
                      onUpdate={(updatedBlock) => handleUpdateBlock(room, updatedBlock)}
                      onRemoveMeasurement={() => handleRemoveBlock(room, item.tempId)}
                    />
                  ))}
                  {/* Add button moved to header */}
                </div>
              </Section>
            );
          })
        )}

        {commonNotes && (
          <div className="mt-4">
            {commonNotes}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 pt-2 border-t border-slate-100 mt-1">
        <div className="flex border border-slate-200 rounded-lg px-4 py-2 items-center justify-between gap-6 bg-white shadow-sm w-full">
          <div className="flex flex-col text-left">
            <span className="text-[11px] text-slate-800 tracking-[0.15em] uppercase block">
              <strong className="font-bold">Estimated Price</strong> may vary in final Quotation
            </span>
          </div>
          <div className="flex items-center justify-end">
            <span className="text-3xl font-black text-slate-900">₹</span>
            <input 
              type="number"
              value={overrideGrandTotal !== '' ? overrideGrandTotal : calculateGrandTotal()}
              onChange={(e) => setOverrideGrandTotal(e.target.value === '' ? '' : e.target.value)}
              className="w-32 sm:w-48 text-3xl font-black text-slate-900 bg-transparent border-none p-0 focus:outline-none focus:ring-0 text-right"
              style={{ padding: 0 }}
            />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full justify-between">
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-bold transition-all duration-300 shadow-sm"
          >
            Cancel
          </button>
          
          <button
            type="button"
            onClick={handleSubmit}
            className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out shadow-sm border border-slate-900 flex items-center justify-center gap-2"
          >
            Preview Quotation
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InitialQuotationForm;
